/**
 * PWA helpers: service worker registration and the browser's install prompt.
 *
 * Chrome/Edge/Android fire `beforeinstallprompt` once the site is installable
 * (manifest + service worker). We keep that event so our own "Install app"
 * button can show the browser's install dialog later. Safari on iPhone has no
 * such event: there the user adds the app with Share > Add to Home Screen.
 */

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(Boolean(deferredPrompt)));

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);

export const isIosSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return iOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

/** Start listening for the install prompt. Call once at startup. */
export function initInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    // Keep the event so the Install button can show it; Chrome still shows its
    // own install icon in the address bar.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export const canPromptInstall = () => Boolean(deferredPrompt);

/** Show the browser's install dialog. Resolves to "accepted", "dismissed" or "unavailable". */
export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  const event = deferredPrompt;
  deferredPrompt = null;
  notify();
  event.prompt();
  try {
    const choice = await event.userChoice;
    return choice?.outcome || "dismissed";
  } catch {
    return "dismissed";
  }
}

export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Register /sw.js in production builds (never in dev, where it would cache the dev server). */
export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
